/*!
 * PatternFly Elements: PfeNumber 1.3.0
 * @license
 * Copyright 2021 Red Hat, Inc.
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 * 
*/

// Import polyfills: isNaN
import "./polyfills--pfe-number.js";

import PFElement from "../../pfelement/dist/pfelement.js";
import numeral from "numeral";

// use thin spaces to separate thousands chunks
numeral.locales.en.delimiters.thousands = " ";

class PfeNumber extends PFElement {

  // Injected at build-time
  static get version() {
    return "1.3.0";
  }

  // Injected at build-time
  get html() {
    return `
<style>:host{display:inline;white-space:nowrap} /*# sourceMappingURL=pfe-number.min.css.map */</style>
<span></span>`;
  }

  static get tag() {
    return "pfe-number";
  }

  get styleUrl() {
    return "pfe-number.scss";
  }

  get templateUrl() {
    return "pfe-number.html";
  }

  static get properties() {
    return {
      number: {
        type: Number,
        title: "Number",
        observer: "_updateNumber"
      },

      _type: {
        type: String,
        title: "Type",
        observer: "_determineFormat"
      },

      format: {
        type: String,
        title: "Custom format",
        observer: "_updateNumber"
      }
    };
  }

  static get types() {
    return {
      abbrev: "0a", // or 'approx'?
      ordinal: "0o",
      percent: "0%",
      bytes: "0[.][00] ib",
      e: "0[.00]e+0",
      thousands: "0,0[.][00]"
    };
  }

  constructor() {
    super(PfeNumber);
  }

  connectedCallback() {
    super.connectedCallback();

    this._determineFormat();
    this._setInitialNumber();
  }

  _setInitialNumber() {
    const numberContentDefined = !isNaN(parseFloat(this.textContent));

    if (numberContentDefined) {
      // this.setAttribute("number", this.textContent);
      this.number = this.textContent;
    }
  }

  _determineFormat() {
    if (this._type && this.constructor.types[this._type]) {
      this.format = this.constructor.types[this._type];
    } else {
      this.format = this.format || "0";
    }
  }

  _updateNumber() {
    if (!this.number || isNaN(parseFloat(this.number))) {
      this.textContent = "";
      this.shadowRoot.querySelector("span").textContent = "";

      return;
    }

    this.textContent = this.number;
    this.shadowRoot.querySelector("span").textContent = this._format(this.number, this.format);
  }

  _format(num, formatString) {
    return numeral(num).format(formatString);
  }
}

PFElement.create(PfeNumber);
